import { useDispatch } from "react-redux";
import { createColumnHelper, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, TableState, useReactTable } from "@tanstack/react-table";
import { DISPLAY_DATE_WITH_TIME } from "@constants/date-formats";
import { resources } from "@constants/hardcoded-strings";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { useTableFilters } from "@hooks/use-table-filters";
import { FormattedAxnSession } from "@idw-types/data/management-console/axn";
import { formatDateToString, sortDates } from "@utils/date-util";
import { sortStrings } from "@utils/string-util";
import { IdwTable } from "@containers/common/idw-table/idw-table";
import { IdwTableContainer } from "@containers/common/idw-table-container/idw-table-container";
import { PaginationContainer } from "@containers/common/pagination-container/pagination-container";
import styles from "./axn-sessions-table-container.less";
import { handleSetColumnFiltersChange, handleSetPaginationChange, handleSetSortingChange } from "@utils/react-table/react-table-util";
import { useMemo } from "react";

type AxnSessionsTableContainerProps = {
    sessions: Array<FormattedAxnSession>;
    tableState: TableState;
    isPageHeight: boolean;
};

export const AxnSessionsTableContainer = (props: AxnSessionsTableContainerProps) => {
    const { sessions, tableState, isPageHeight } = props;
    const dispatch = useDispatch();

    const columnHelper = createColumnHelper<FormattedAxnSession>();
    const columns = useMemo(() => ([
        columnHelper.accessor("asi", {
            header: resources.tables.axnSessions.columns.transactionId.header,
            filterFn: "fuzzyText"
        }),
        columnHelper.accessor("mbun", {
            header: resources.tables.axnSessions.columns.mbun.header,
            filterFn: "fuzzyText"
        }),
        columnHelper.accessor("credential", {
            header: resources.tables.axnSessions.columns.credential.header,
            filterFn: "fuzzyText"
        }),
        columnHelper.accessor("apiKey", {
            header: resources.tables.axnSessions.columns.apiKey.header,
            filterFn: "fuzzyText"
        }),
        columnHelper.accessor("relyingParty", {
            header: resources.tables.axnSessions.columns.relyingParty.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.relyingParty, b.original.relyingParty)
        }),
        columnHelper.accessor("date", {
            header: resources.tables.axnSessions.columns.date.header,
            filterFn: "dateTimeFilter",
            sortingFn: (a, b) => sortDates(a.original.date, b.original.date),
            cell: (props) => props.getValue() ? <span>{formatDateToString(props.getValue(), DISPLAY_DATE_WITH_TIME)}</span> : null
        })
    ]), [sessions]);

    const { filterTypes } = useTableFilters();

    const useTableResult = useReactTable({
        columns,
        data: sessions,
        state: tableState,
        filterFns: filterTypes,
        sortDescFirst: false,
        autoResetPageIndex: true,
        onSortingChange: (sortingOrUpdateFn) => handleSetSortingChange(sortingOrUpdateFn, TableSettingsTableTypes.AXN_SESSIONS, tableState.sorting, dispatch),
        onColumnFiltersChange: (filtersOrUpdateFn) => handleSetColumnFiltersChange(filtersOrUpdateFn, TableSettingsTableTypes.AXN_SESSIONS, tableState.columnFilters, dispatch),
        onPaginationChange: (paginationOrUpdateFn) => handleSetPaginationChange(paginationOrUpdateFn, TableSettingsTableTypes.AXN_SESSIONS, tableState.pagination, dispatch),
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    });

    return (
        <IdwTableContainer className={styles.axnSessionsTableContainer}>
            <IdwTable
                id="axn-sessions"
                useTableResult={useTableResult}
                headerColumnKeyPrefix={"axn-sessions"}
                className={styles.axnSessionsTable}
            />
            <PaginationContainer
                idPrefix="axn-sessions"
                useTableResult={useTableResult}
                isPageHeight={isPageHeight}
            />
        </IdwTableContainer>
    );
};